import { useEffect } from "react";
import { CONTACT, BRAND, MARKETS } from "../data/content";
import { useLang, LANGS } from "../i18n";

const ORIGIN = window.location.origin;
const DEFAULT_OG = `${ORIGIN}/assets/hero.webp`;

const OG_LOCALE = {
  en: "en_GB", sv: "sv_SE", pt: "pt_PT", pl: "pl_PL", fr: "fr_BE", es: "es_ES",
  de: "de_DE", da: "da_DK", no: "nb_NO", fi: "fi_FI", nl: "nl_NL",
};

const ISO = { se: "SE", gb: "GB", pt: "PT", pl: "PL", be: "BE", es: "ES", de: "DE", dk: "DK", no: "NO", fi: "FI", sa: "SA" };

const ORG = {
  "@context": "https://schema.org",
  "@type": ["Organization", "ProfessionalService"],
  "@id": `${ORIGIN}/#organization`,
  name: BRAND.name, url: ORIGIN, logo: `${ORIGIN}/assets/logo.png`, image: DEFAULT_OG,
  email: CONTACT.email, telephone: CONTACT.phones.map((p) => p.number),
  foundingDate: String(CONTACT.foundedYear), slogan: BRAND.tagline,
  description: "Sweden-based European workforce and industrial services partner — white-collar recruitment, skilled blue-collar workforce and HSE consultancy across Europe and selected international markets.",
  address: { "@type": "PostalAddress", postalCode: CONTACT.postalCode, addressLocality: CONTACT.city, addressCountry: "SE" },
  areaServed: MARKETS.map((m) => ({ "@type": "Country", name: m.mapName, identifier: ISO[m.code] })),
  knowsLanguage: LANGS.map((l) => l.name),
  contactPoint: [
    { "@type": "ContactPoint", telephone: CONTACT.phone, contactType: "sales", areaServed: MARKETS.map((m) => ISO[m.code]), availableLanguage: LANGS.map((l) => l.name) },
    { "@type": "ContactPoint", email: CONTACT.hrEmail, contactType: "recruiting" },
  ],
  sameAs: [CONTACT.linkedin, CONTACT.googleBusiness, CONTACT.x].filter(Boolean),
  vatID: CONTACT.orgNumber,
  identifier: { "@type": "PropertyValue", name: "VAT number", value: CONTACT.orgNumber },
};

const WEBSITE = {
  "@context": "https://schema.org", "@type": "WebSite", "@id": `${ORIGIN}/#website`,
  url: ORIGIN, name: BRAND.name, publisher: { "@id": `${ORIGIN}/#organization` },
  inLanguage: LANGS.map((l) => l.code),
};

const upsertMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (id, rel, href, hreflang) => {
  if (rel === "canonical") {
    document.head.querySelectorAll("link[rel='canonical']:not([data-nv])").forEach((el) => el.remove());
  }
  let el = document.head.querySelector(`link[data-nv="${id}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("data-nv", id);
    el.rel = rel;
    if (hreflang) el.hreflang = hreflang;
    document.head.appendChild(el);
  }
  el.href = href;
};

const upsertLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(data);
};

const crumbs = (pathname) => {
  const parts = pathname.split("/").filter(Boolean);
  const items = [{ "@type": "ListItem", position: 1, name: "Home", item: `${ORIGIN}/` }];
  parts.forEach((part, i) => {
    items.push({
      "@type": "ListItem", position: i + 2,
      name: part.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      item: `${ORIGIN}/${parts.slice(0, i + 1).join("/")}`,
    });
  });
  return { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: items };
};

export const Seo = ({ title, description, image, type = "website", noindex = false, schema = null }) => {
  const { lang } = useLang();
  useEffect(() => {
    const path = window.location.pathname;
    const url = ORIGIN + path;
    const img = image || DEFAULT_OG;

    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1");
    upsertMeta("name", "googlebot", noindex ? "noindex,nofollow" : "index,follow");

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:site_name", BRAND.name);
    upsertMeta("property", "og:image", img);
    upsertMeta("property", "og:image:alt", BRAND.name);
    upsertMeta("property", "og:locale", OG_LOCALE[lang] || "en_GB");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);
    upsertMeta("name", "twitter:site", "@thenordicvision");

    upsertLink("canonical", "canonical", url);
    LANGS.forEach((l) => upsertLink(`alt-${l.code}`, "alternate", `${url}?lang=${l.code}`, l.code));
    upsertLink("alt-default", "alternate", url, "x-default");

    upsertLd("nv-org-ld", ORG);
    upsertLd("nv-website-ld", WEBSITE);
    upsertLd("nv-breadcrumb-ld", crumbs(path));
    if (schema) upsertLd("nv-page-ld", schema);
  }, [title, description, image, type, noindex, schema, lang]);

  useEffect(() => {
    const alts = LANGS.filter((l) => l.code !== lang).map((l) => OG_LOCALE[l.code]);
    document.head.querySelectorAll('meta[property="og:locale:alternate"]').forEach((el) => el.remove());
    alts.forEach((loc) => {
      const el = document.createElement("meta");
      el.setAttribute("property", "og:locale:alternate");
      el.setAttribute("content", loc);
      document.head.appendChild(el);
    });
  }, [lang]);

  return null;
};

export default Seo;
